import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { Toggle } from '@flipster/react/design-system/toggle';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import EarnIcon from '@flipster/react/icon/generated/EarnIcon';
import PieChartIcon from '@flipster/react/icon/generated/PieChartIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { PusdtCurrency } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import { formatToShortNumber } from '@flipster/utils/num';

import { pusdtAutoConversionBunja } from './state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const PusdtAutoConversionModal = ({ isOpen, onClose, onExit }: Props) => {
  const t = useTranslations();
  const { isAutoConversionPusdtEnabledAtom, mutateAutoConversionPusdtAtom } = useBunja(pusdtAutoConversionBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const isAutoConversionPusdtEnabled = useAtomValue(isAutoConversionPusdtEnabledAtom);
  const { mutateAsync, isPending: isMutatePending } = useAtomValue(mutateAutoConversionPusdtAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { maxCap } = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary);

  const handleToggle = async () => {
    try {
      const nextEnabledState = !isAutoConversionPusdtEnabled;
      await mutateAsync({ enabled: nextEnabledState });
      if (nextEnabledState === true) {
        snackbar.success({ message: t('Earn.r260220.Auto_subscription_on_toast') });
      } else {
        snackbar.success({ message: t('Earn.r260220.Auto_subscription_off_toast') });
      }
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      onClose();
    }
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} mode='bottomSheet'>
      <Modal.Header>
        <Modal.Title label={t('Earn.r260220.Pusdt_auto_subscribe_to_premium_earn_usdt')} />
      </Modal.Header>
      <Modal.Content>
        <div
          className={hstack({
            justify: 'space-between',
            p: 16,
            rounded: 8,
            bg: 'background.default.level-1',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-1',
            mb: 12,
          })}
        >
          <div className={stack()}>
            <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>
              {!isMutatePending && isAutoConversionPusdtEnabled
                ? t('Earn.r260220.Pusdt_auto_subscription_is_on')
                : t('Earn.r260220.Pusdt_auto_subscription_is_off')}
            </p>
            <p
              className={css({
                textStyle: 'Text/M/Regular',
                color:
                  isAutoConversionPusdtEnabled || isMutatePending ? 'content.default.level-2' : 'content.accent.info',
              })}
            >
              {isMutatePending
                ? 'Updating...'
                : isAutoConversionPusdtEnabled
                  ? t('Earn.r260220.Pusdt_earn_more_with_auto_subscription')
                  : t('Earn.r260220.Pusdt_turn_on_to_automate_your_subscription')}
            </p>
          </div>
          <Toggle selected={isAutoConversionPusdtEnabled} onChange={handleToggle} disabled={isMutatePending} />
        </div>
        <div className={stack()}>
          <Item
            icon={<EarnIcon />}
            label={t('Earn.r260318.Pusdt_automatically_put_your_idle_usdt_to_work')}
            description={t('Earn.r260220.Pusdt_all_available_usdt_excluding_margin_will_be_added_to_premium_earn_usdt')}
          />
          <Item
            icon={<PieChartIcon />}
            label={`${t('Earn.r260220.Pusdt_auto_subscription_up_to_limit', { limit: formatToShortNumber(maxCap.toString()) })} USDT`}
            description={t('Earn.r260318.Pusdt_excess_funds_wont_be_autosubscribed')}
          />
          <Item icon={<ClockIcon />} label={t('Earn.r260220.Pusdt_daily_auto_subscription_at_0200_UTC')} />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          label={t('Autogen_common.r251126.s_Dismiss')}
          hierarchy='secondary'
          color='neutral'
          size='large'
          onClick={onClose}
          css={{ w: '100%' }}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

const Item = ({ icon, label, description }: { icon: ReactNode; label: string; description?: string }) => {
  return (
    <div className={hstack({ py: 8, gap: 16 })}>
      {icon}
      <div className={stack({ gap: 2 })}>
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>{label}</p>
        {description && (
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>{description}</p>
        )}
      </div>
    </div>
  );
};
